import React, { useRef } from 'react';
import { Input, Button, Typography, Tooltip, Upload } from 'antd';
import {
  SendOutlined,
  PaperClipOutlined,
  CloseOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  LoadingOutlined,
  StopOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import { formatFileSize } from './utils';

const { Text } = Typography;

// Get file icon based on content type
const getFileIcon = (contentType) => {
  if (!contentType) return <FileOutlined />;
  if (contentType.startsWith('image/')) return <FileImageOutlined style={{ color: '#52c41a' }} />;
  if (contentType.includes('pdf')) return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
  if (contentType.includes('sheet') || contentType.includes('excel')) return <FileExcelOutlined style={{ color: '#52c41a' }} />;
  if (contentType.includes('word') || contentType.includes('document')) return <FileWordOutlined style={{ color: '#1890ff' }} />;
  return <FileOutlined style={{ color: '#8c8c8c' }} />;
};

/**
 * ChatInput - Modern chat input with file upload
 */
export default function ChatInput({
  value,
  onChange,
  onSend,
  onCancel,
  processing,
  uploadedFile,
  uploading,
  isDragging,
  onFileUpload,
  onRemoveFile,
  onDragOver,
  onDragLeave,
  onDrop
}) {
  const { t } = useTranslation();
  const inputRef = useRef(null);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      if (e.nativeEvent.isComposing) return; // IME composition in progress, don't send
      const { selectionStart, selectionEnd } = e.target;
      if (selectionStart !== selectionEnd) return; // text is selected, don't send
      e.preventDefault();
      onSend();
    }
  };

  const canSend = !isEmpty(value?.trim()) || uploadedFile;

  return (
    <div style={{ padding: '12px 20px 16px', backgroundColor: '#fafaf8' }}>
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: uploadedFile ? '12px 10px 10px 14px' : '6px 6px 6px 14px',
          borderRadius: '24px',
          border: isDragging ? '1px solid #000000' : '1px solid #e5e4df',
          backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.03)' : '#ffffff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
          transition: 'all 0.15s ease'
        }}
      >
        {/* File preview chip */}
        {uploadedFile && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              marginBottom: '8px',
              marginLeft: '4px',
              backgroundColor: '#fff',
              borderRadius: '12px',
              border: '1px solid #e8e8e8',
              maxWidth: 'fit-content'
            }}
          >
            {getFileIcon(uploadedFile.content_type)}
            <Text ellipsis style={{ fontSize: '13px', maxWidth: '200px', color: '#333' }}>
              {uploadedFile.filename}
            </Text>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined style={{ fontSize: '10px' }} />}
              onClick={onRemoveFile}
              style={{
                color: '#bbb',
                padding: 0,
                minWidth: '20px',
                height: '20px'
              }}
            />
          </div>
        )}

        {/* Input row */}
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          {/* Attach button */}
          <Upload
            beforeUpload={onFileUpload}
            showUploadList={false}
            disabled={processing || uploading}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
          >
            <Tooltip title={t('operator.ai_chat.attach_file', 'Attach file')} placement="top">
              <Button
                type="text"
                icon={uploading ? <LoadingOutlined spin /> : <PaperClipOutlined style={{ fontSize: '20px' }} />}
                disabled={processing}
                style={{
                  color: '#666',
                  padding: '6px',
                  height: 'auto',
                  border: 'none',
                  background: 'transparent'
                }}
              />
            </Tooltip>
          </Upload>

          {/* Input field */}
          <Input.TextArea
            ref={inputRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isDragging
                ? t('operator.ai_chat.drop_file', 'Drop file here...')
                : t('operator.ai_chat.input_placeholder', 'Type a message...')
            }
            disabled={processing}
            variant="borderless"
            autoSize={{ minRows: 1, maxRows: 6 }}
            style={{
              flex: 1,
              fontSize: '14px',
              padding: '8px 10px',
              backgroundColor: 'transparent',
              color: '#1f2937',
              resize: 'none'
            }}
          />

          {/* Cancel button (while processing) or Send button */}
          {processing ? (
            <Button
              type="text"
              shape="circle"
              icon={<StopOutlined style={{ fontSize: '14px', color: '#fff' }} />}
              onClick={onCancel}
              style={{
                minWidth: '34px',
                height: '34px',
                backgroundColor: '#ff4d4f',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          ) : (
            <Button
              type="text"
              shape="circle"
              icon={<SendOutlined style={{ fontSize: '14px', color: '#fff' }} />}
              onClick={onSend}
              disabled={!canSend || isDragging}
              style={{
                minWidth: '34px',
                height: '34px',
                backgroundColor: canSend ? '#000000' : '#d9d9d5',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
